import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  InputLabel,
  FormControl,
  TableSortLabel,
  TableFooter,
} from "@mui/material";
import { Edit as EditIcon, Visibility as ViewIcon, Add as AddIcon, Download } from "@mui/icons-material";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DownOutlined } from "@ant-design/icons";

export default function MantisDataTable({
  title,
  data = [],
  columns = [],
  filters = [],
  onFilterChange,
  onFilterSubmit,
  onExport,
  onPrint,
  searchable = false,
  searchPlaceholder = "Search...",
  showAddButton = false,
  onAdd,
  footerTotals,
  isLoading = false,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [filterForm, setFilterForm] = useState({});
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");

  // Add serial number column
  const tableColumns = [{ label: "S.No", type: "text" }, ...columns];

  useEffect(() => {
    if (searchable && searchTerm) {
      const lower = searchTerm.toLowerCase();
      setFilteredData(
        data.filter((row) =>
          Object.values(row).some((val) => String(val).toLowerCase().includes(lower))
        )
      );
    } else {
      setFilteredData(data);
    }
  }, [searchTerm, data, searchable]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...filterForm, [name]: value };
    setFilterForm(updated);
    onFilterChange && onFilterChange(updated);
  };

  const handleFilterSubmit = () => {
    for (const f of filters || []) {
      if ((f.type === "select" || f.type === "date") && !filterForm[f.name]) {
        toast.error(`Select ${f.label}`);
        return;
      }
    }
    onFilterSubmit && onFilterSubmit(filterForm);
  };

  // Format date range labels using plain JS
  const formatDateRange = (value) => {
    if (!value) return "";
    const [start, end] = value.split(",");
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    try {
      return `${new Date(start).toLocaleDateString(undefined, opts)} → ${new Date(
        end
      ).toLocaleDateString(undefined, opts)}`;
    } catch {
      return value; // fallback if invalid date
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  // Sort filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!orderBy) return 0;
    const valA = a[orderBy] ?? "";
    const valB = b[orderBy] ?? "";

    if (typeof valA === "number" && typeof valB === "number") {
      return order === "asc" ? valA - valB : valB - valA;
    }

    return order === "asc"
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

const isColumnNumeric = (key) => {
  if (!key || !filteredData.length) return false;

  // helper to validate a numeric-like cell (allow commas and decimals)
  const isNumericValue = (val) => {
    if (val === null || val === undefined) return true;
    if (typeof val === "number") return true;
    const s = String(val).trim();
    if (s === "") return false;
    const cleaned = s.replace(/,/g, "");
    return /^[+-]?\d+(\.\d+)?$/.test(cleaned);
  };

  // Column considered numeric only if ALL non-empty rows are numeric
  return filteredData.every((row) => {
    const val = row[key];
    // Decide: treat empty/null as non-numeric (so mixed -> no total)
    return isNumericValue(val);
  });
};

  return (
    
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Paper sx={{ paddingTop: 1, paddingLeft: 2, paddingRight: 2 }}>
      {title && <h3>{title}</h3>}

      {/* Filters + Search + Export */}
      {(filters.length > 0 || searchable || onExport || onPrint) && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            marginBottom: "1rem",
            alignItems: "center",
          }}
        >
          {filters.map((f, i) => (
  <FormControl key={i} size="small" sx={{ minWidth: 150 }}>
    <InputLabel shrink>{f.label}</InputLabel>

    {f.type === "select" ? (
      <Autocomplete
        size="small"
        options={f.options || []}
        getOptionLabel={(opt) =>
          f.name.toLowerCase().includes("range")
            ? formatDateRange(opt.value)
            : opt.label || ""
        }
        value={f.options?.find((opt) => opt.value === filterForm[f.name]) || null}
        onChange={(_, newValue) => {
          const updated = { ...filterForm, [f.name]: newValue?.value || "" };
          setFilterForm(updated);
          onFilterChange && onFilterChange(updated);
        }}
        renderInput={(params) => (
          <TextField {...params} label={f.label} placeholder={`Search ${f.label}`} />
        )}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
      />
    ) : f.type === "date" ? (
    <DatePicker
      label={f.label}
      value={filterForm[f.name] ? new Date(filterForm[f.name]) : null}
      onChange={(newValue) => {
        if (newValue) {
          // Create a date string in local timezone (YYYY-MM-DD)
          const year = newValue.getFullYear();
          const month = String(newValue.getMonth() + 1).padStart(2, "0");
          const day = String(newValue.getDate()).padStart(2, "0");
          const localDate = `${year}-${month}-${day}`;

          const updated = { ...filterForm, [f.name]: localDate };
          setFilterForm(updated);
          onFilterChange && onFilterChange(updated);
        }
      }}
      slotProps={{
        textField: {
          fullWidth: true,
          variant: "outlined",
          size: "small",
        },
      }}
    />
    ) : (
      <TextField
        type={f.type || "text"}
        name={f.name}
        value={filterForm[f.name] || ""}
        onChange={handleFilterChange}
        label={f.label}
        {...(f.InputLabelProps ? { InputLabelProps: f.InputLabelProps } : {})}
      />
    )}
  </FormControl>
))}

          {onFilterSubmit && (
            <Button variant="contained" onClick={handleFilterSubmit}>
              Apply
            </Button>
          )}

          {searchable && (
            <TextField
              size="small"
              label={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 250 }}
            />
          )}
            {onExport && (
            <Button variant="outlined" onClick={onExport}>
              Export
            </Button>
          )}
         {onPrint && (
            <Button variant="outlined" color="secondary" onClick={onPrint}>
            Print / PDF
            </Button>
         )}
        </div>
      )}

      {/* Table */}
        <TableContainer component={Paper} sx={{
          height: "calc(100vh - 230px)",
          overflow: "auto",
          position: "relative"
        }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {tableColumns.map((col, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    backgroundColor: "#c8e0f8ff",
                    color: "black",
                    fontWeight: "bold",
                    cursor: col.key ? "pointer" : "default",
                  }}
                >
                  {col.key ? (
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : "asc"}
                      onClick={() => {
                        const isAsc = orderBy === col.key && order === "asc";
                        setOrder(isAsc ? "desc" : "asc");
                        setOrderBy(col.key);
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((row, i) => (
                <TableRow key={i} hover>
                  {tableColumns.map((col, j) => {
                    if (j === 0) {
                      return <TableCell key={j}>{i + 1}</TableCell>;
                    }
                    if (col.type === "actions" && col.actions) {
                      return (
                        <TableCell key={j}>
                          {col.actions.map((action, idx) => {
                            if (action.type === "edit") {
                              return (
                                <IconButton key={idx} color="primary" onClick={() => action.action(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              );
                            }
                            if (action.type === "view") {
                              return (
                                <IconButton key={idx} color="success" onClick={() => action.action(row)}>
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              );
                            }
                             if (action.type === "download") {
                              return (
                                <IconButton key={idx} color="success" onClick={() => action.action(row)}>
                                  <Download fontSize="small" />
                                </IconButton>
                              );
                            }
                            return (
                              <IconButton key={idx} onClick={() => action.action(row)}>
                                {action.icon}
                              </IconButton>
                            );
                          })}
                        </TableCell>
                      );
                    }
                    if (col.type === "badge") {
                      const value = row[col.key || ""];
                      let displayText = value;
                      if (typeof value === "boolean") {
                        displayText = value ? "Active" : "Inactive";
                      }
                      if (value == "Y") {
                        displayText = value ? "Active" : "Inactive";
                      }

                      const normalizedValue =
                        typeof value === "boolean" ? value.toString() : value;

                      return (
                        <TableCell key={j}>
                          <Chip
                            size="small"
                            label={displayText}
                            color={
                              normalizedValue === "Active" ||
                              normalizedValue === "P" ||
                              normalizedValue === "Y" ||
                              normalizedValue === "true"
                                ? "success"
                                : normalizedValue === "Pending" ||
                                  normalizedValue === "A" ||
                                  normalizedValue === "N" ||
                                  normalizedValue === "false"
                                ? "error"
                                : "default"
                            }
                          />
                        </TableCell>
                      );
                    }
                    if (col.type === "custom" && col.render) {
                      return <TableCell key={j}>{col.render(row)}</TableCell>;
                    }
                    return <TableCell key={j}>{row[col.key || ""] ?? "-"}</TableCell>;
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableColumns.length} align="center">
                  No records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody> 
 {footerTotals && filteredData.length > 0 && (
  <TableFooter
    sx={{
      position: "sticky",
      bottom: 0,
      zIndex: 2,
      backgroundColor: "#c8e0f8ff",
    }}
  >
    <TableRow>
      {tableColumns.map((col, colIndex) => {

        if (colIndex === 0) {
          return (
            <TableCell
              key={colIndex}
              sx={{
                position: "sticky",
                bottom: 0,
                fontWeight: "bold",
                backgroundColor: "#c8e0f8ff",
                color: "black",
                zIndex: 3
              }}
            >
              TOTAL
            </TableCell>
          );
        }

        if (colIndex === 1) {
          return (
            <TableCell
              key={colIndex}
              sx={{
                position: "sticky",
                bottom: 0,
                fontWeight: "bold",
                backgroundColor: "#c8e0f8ff",
                color: "black",
                zIndex: 3
              }}
            >
              {/* BLANK instead of '-' */}
            </TableCell>
          );
        }

        const key = col.key;

        const shouldShowTotal =
          key &&
          Array.isArray(footerTotals.totalKeys) &&
          footerTotals.totalKeys.includes(key) &&
          isColumnNumeric(key);

        const totalValue = shouldShowTotal
          ? footerTotals.totalsByKey[key] ?? ""
          : "";

        return (
          <TableCell
            key={colIndex}
            sx={{
              position: "sticky",
              bottom: 0,
              fontWeight: "bold",
              backgroundColor: "#c8e0f8ff",
              color: "black",
              zIndex: 3,
              textAlign: "left",
            }}
          >
            {totalValue}
          </TableCell>
        );
      })}
    </TableRow>
  </TableFooter>
)}





        </Table>
      </TableContainer>

 {isLoading && (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(255, 255, 255, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          background: "#fff",
          padding: "1rem 2rem",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <CircularProgress size={24} thickness={4} />
        <span style={{ fontSize: 14, color: "#444" }}>Loading data...</span>
      </div>
    </div>)}
      {/* Floating Add Button */}
      {showAddButton && (
        <IconButton
          onClick={onAdd}
          color="primary"
          sx={{
            position: "fixed",
            bottom: 40,
            right: 40,
            bgcolor: "primary.main",
            color: "#fff",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          <AddIcon />
        </IconButton>
      )}
    </Paper>
    </LocalizationProvider>
  );
}
